import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { CONTACT } from "@/data/content";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/pricing", label: "Pricing" },
  { to: "/process", label: "Process" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

const Logo = () => (
  <span className="flex items-center gap-2.5">
    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-ink font-display text-sm font-bold text-white">MG</span>
    <span className="flex flex-col leading-none">
      <span className="font-display text-lg font-bold tracking-tight text-ink">My Geeks LLC</span>
      <span className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">Business Management &amp; Web Development</span>
    </span>
  </span>
);

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header data-testid="site-navbar" className="sticky top-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-[60] focus:rounded-md focus:bg-brand focus:px-3 focus:py-2 focus:text-xs focus:font-semibold focus:text-white">
        Skip to content
      </a>
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6">
        <Link to="/" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <Logo />
        </Link>

        <nav aria-label="Primary" className="hidden items-center gap-1 lg:flex" data-testid="nav-desktop-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `rounded-lg px-3.5 py-2 text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-slate-100 text-ink" : "text-slate-600 hover:bg-slate-50 hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link to="/contact" data-testid="nav-quote-button" className="btn-primary ml-3 px-5 py-2.5">
            Request a Quote
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="flex items-center rounded-lg p-2 text-ink hover:bg-slate-100 lg:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            aria-label="Mobile"
            key={location.pathname + "-mobile"}
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-200 bg-white lg:hidden"
          >
            <div className="space-y-1 p-3">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block rounded-lg px-4 py-3 text-sm font-medium ${isActive ? "bg-slate-100 text-ink" : "text-slate-700 hover:bg-slate-50"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="grid grid-cols-2 gap-2 pt-2">
                <Link to="/contact" onClick={() => setOpen(false)} data-testid="nav-mobile-quote-button" className="btn-primary py-3">Request a Quote</Link>
                <a href={CONTACT.phoneHref} data-testid="nav-mobile-call-button" className="btn-outline py-3">Call {CONTACT.phone}</a>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
